/**
 * AskFiltersContext - exposes the rail's per-ask filters
 * (document scope, response language) to the Composer's send
 * mutation.
 *
 * Document filter: a Set of selected Vertex doc ids. Empty Set
 * means "all documents". When the user picks a group from the
 * dropdown, we expand the group into its constituent doc ids
 * before adding to the Set.
 *
 * Language: ISO 639-1 code or '' (auto-detect). Persisted to
 * localStorage so the user's last language choice survives
 * reloads. Document selection is per-session - picking docs
 * once doesn't lock the next visit into the same scope.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  LANGUAGE_CODE_MIGRATIONS,
  SUPPORTED_LANGUAGE_CODES,
} from './LanguagePicker';

const LANG_STORAGE_KEY = 'platform-ui:ask-language';

interface AskFiltersContextValue {
  /** Selected Vertex doc ids. Empty means "all". */
  documentIds: Set<string>;
  setDocumentIds: (next: Set<string>) => void;
  /** ISO 639-1 code or '' (auto-detect). */
  language: string;
  setLanguage: (next: string) => void;
  clearAll: () => void;
}

const AskFiltersContext = createContext<AskFiltersContextValue | null>(null);

function readInitialLanguage(): string {
  let raw = '';
  try {
    raw = localStorage.getItem(LANG_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
  if (!raw) return '';
  // Migrate known-renamed codes (e.g. 'zh-cn' → 'zh') in place so
  // existing users don't lose a valid pick when we rename a code.
  const migrated = LANGUAGE_CODE_MIGRATIONS[raw] ?? raw;
  // Drop anything that's no longer in the supported set (e.g. a
  // language we removed from the picker). Falling back to '' (Auto)
  // is safer than letting the server receive an unsupported code
  // and silently fall through to en-US for TTS/STT.
  return SUPPORTED_LANGUAGE_CODES.has(migrated) ? migrated : '';
}

export function AskFiltersProvider({ children }: { children: ReactNode }) {
  const [documentIds, setDocumentIdsState] = useState<Set<string>>(
    () => new Set(),
  );
  const [language, setLanguageState] = useState<string>(readInitialLanguage);

  useEffect(() => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, language);
    } catch {
      /* private mode - choice just won't persist. */
    }
  }, [language]);

  const setDocumentIds = useCallback((next: Set<string>) => {
    // Always make a fresh Set so React picks up the change -
    // mutating a Set in place wouldn't trigger a re-render.
    setDocumentIdsState(new Set(next));
  }, []);

  const setLanguage = useCallback((next: string) => {
    setLanguageState(next);
  }, []);

  const clearAll = useCallback(() => {
    setDocumentIdsState(new Set());
    // Don't reset language - user might still want their preferred
    // reply language.
  }, []);

  const value = useMemo(
    () => ({
      documentIds,
      setDocumentIds,
      language,
      setLanguage,
      clearAll,
    }),
    [documentIds, setDocumentIds, language, setLanguage, clearAll],
  );

  return (
    <AskFiltersContext.Provider value={value}>
      {children}
    </AskFiltersContext.Provider>
  );
}

export function useAskFilters(): AskFiltersContextValue {
  const ctx = useContext(AskFiltersContext);
  if (!ctx) {
    throw new Error('useAskFilters must be used inside <AskFiltersProvider>');
  }
  return ctx;
}
